import { Button } from "@light-technology/components";
import { BatteryFull, Car02, Copy01, LinkExternal01 } from "@untitledui/icons";
import { format, parse } from "date-fns";
import { Fragment } from "react";
import { toast } from "sonner";
import { type Program, ProgramType } from "../../state/api.types";
import { formatCents, formatCurrency } from "../../utilities/currency";
import { toTitleCase } from "../../utilities/stringHelpers";

type ProgramDesignProps = {
  program: Program;
};

const DesignField = ({
  title,
  value,
}: {
  title: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-1 flex-col gap-1.5">
    <div className="text-sm font-medium text-secondary">{title}</div>
    <div className="text-sm text-primary">{value}</div>
  </div>
);

const ProgramTypeLabel = ({ programType }: { programType: ProgramType }) => {
  switch (programType) {
    case ProgramType.Battery:
      return (
        <div className="flex items-center gap-1">
          <BatteryFull className="size-4 -rotate-90" />
          <span>Battery program</span>
        </div>
      );
    case ProgramType.Evcharging:
      return (
        <div className="flex items-center gap-1">
          <Car02 className="size-4" />
          <span>EV charging program</span>
        </div>
      );
    default:
      return <span>{programType}</span>;
  }
};

const formatOwnership = (ownershipType: string) =>
  toTitleCase(ownershipType.replace(/_/g, " "));

const hasNonZeroValue = (value: string | null | undefined) =>
  value !== null && value !== undefined && Number(value) !== 0;

const BatteryDesignFields = ({ program }: ProgramDesignProps) => {
  const battery = program.battery_details;
  if (!battery) return null;

  return (
    <>
      <div className="flex gap-3xl">
        {battery.minimum_capacity_kwh && (
          <DesignField
            title="Minimum capacity"
            value={`${battery.minimum_capacity_kwh} kWh`}
          />
        )}
        {hasNonZeroValue(battery.monthly_credit_dollars) ? (
          <DesignField
            title="Customer credit"
            value={`${formatCurrency(battery.monthly_credit_dollars)} monthly`}
          />
        ) : hasNonZeroValue(battery.monthly_credit_per_kwh_dollars) ? (
          <DesignField
            title="Customer credit"
            value={`${formatCurrency(battery.monthly_credit_per_kwh_dollars)}/kWh monthly`}
          />
        ) : null}
      </div>
      {hasNonZeroValue(battery.monthly_commission_dollars) ? (
        <DesignField
          title="Partner commission"
          value={`${formatCurrency(battery.monthly_commission_dollars)} monthly`}
        />
      ) : hasNonZeroValue(battery.monthly_commission_per_kwh_dollars) ? (
        <DesignField
          title="Partner commission"
          value={`${formatCurrency(battery.monthly_commission_per_kwh_dollars)}/kWh monthly`}
        />
      ) : null}
    </>
  );
};

const formatTime = (time: string) =>
  format(parse(time, "HH:mm", new Date()), "h:mm a");

const EVChargingTypeAndFeeFields = ({ program }: ProgramDesignProps) => {
  const ev = program.ev_charging_details;

  return (
    <div className="flex gap-3xl">
      <DesignField
        title="Type"
        value={<ProgramTypeLabel programType={program.program_type} />}
      />
      {ev?.base_fee && (
        <DesignField title="Charging fee" value={formatCurrency(ev.base_fee)} />
      )}
    </div>
  );
};

const EVChargingDesignFields = ({ program }: ProgramDesignProps) => {
  const ev = program.ev_charging_details;
  if (!ev) return null;

  const schedules = ev.schedules ?? [];
  const primaryRate = (ev.on_schedule_rates ?? []).at(0);

  if (schedules.length === 0 && !primaryRate) return null;

  return (
    <div className="flex gap-3xl">
      {schedules.length > 0 && (
        <DesignField
          title="Charging schedule"
          value={schedules.map((s, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {formatTime(s.start_time)} – {formatTime(s.end_time)}
            </Fragment>
          ))}
        />
      )}
      {primaryRate && (
        <DesignField
          title="Charging rate"
          value={`${formatCents(primaryRate.rate)}¢ per kWh, including delivery fees`}
        />
      )}
    </div>
  );
};

export const ProgramDesign = ({ program }: ProgramDesignProps) => {
  const isEvCharging = program.program_type === ProgramType.Evcharging;

  return (
    <div className="rounded-xl border border-secondary p-3xl">
      <div className="flex flex-col gap-4xl">
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-secondary">Details</div>
          {isEvCharging && (
            <div className="text-sm text-primary">
              Lower bills for EV owners with off-peak charging.
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3xl">
          {isEvCharging ? (
            <EVChargingTypeAndFeeFields program={program} />
          ) : (
            <div className="flex gap-3xl">
              <DesignField
                title="Type"
                value={<ProgramTypeLabel programType={program.program_type} />}
              />
              <DesignField
                title="Device ownership"
                value={formatOwnership(program.ownership_type)}
              />
            </div>
          )}
          {program.program_type === ProgramType.Battery && (
            <BatteryDesignFields program={program} />
          )}
          {isEvCharging && <EVChargingDesignFields program={program} />}
          {program.terms_of_service_url && (
            <div className="flex items-center">
              <DesignField
                title="Terms of service"
                value={program.terms_of_service_url}
              />
              <Button
                size="sm"
                color="tertiary"
                iconLeading={LinkExternal01}
                onClick={() =>
                  window.open(
                    program.terms_of_service_url,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              />
              <Button
                size="sm"
                color="tertiary"
                iconLeading={Copy01}
                onClick={() => {
                  navigator.clipboard.writeText(program.terms_of_service_url);
                  toast.success("URL copied to clipboard");
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
